import { useCallback } from "react";
import { useEffect } from "react";
import { useState, useRef } from "react";

const useDrawCanvas = (canvasRef, value, setValue, sign) => {
    const [isDrawing, setIsDrawing] = useState(false);
    const savedImageData = useRef(null);

    // Función para limpiar el canvas
    const handleClearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Rellenar fondo blanco para que el trazo negro sea siempre visible
        // independientemente del tema claro/oscuro del fondo CSS
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        savedImageData.current = null;
        setValue(null);
    };

    const getPosition = useCallback((event) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        
        const rect = canvas.getBoundingClientRect();

        let clientX, clientY;
        
        // Diferenciar entre evento táctil y de ratón
        if (event.touches && event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }
        
        // Coordenadas CSS puras — funciona correctamente tanto con ctx.scale(dpr)
        // (InputSign) como sin él (ModalSign). No multiplicar por scaleX/scaleY
        // porque causaba doble-escalado en pantallas Retina (strokes fuera del canvas).
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        
        return { x, y };
    },[canvasRef]);

    const startDrawing = useCallback((event) => {
        // Prevenir scroll en móviles mientras se dibuja
        if (event.touches) {
            event.preventDefault();
        }
        
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        // Rellenar fondo blanco al primer trazo para garantizar visibilidad
        // en cualquier tema (el CSS backgroundColor no forma parte del bitmap)
        if (!savedImageData.current) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        setIsDrawing(true);
        ctx.beginPath();
        const { x, y } = getPosition(event);
        ctx.moveTo(x, y);
    },[canvasRef, getPosition, savedImageData]);

    const draw = useCallback((event) => {
        if (!isDrawing) return;

        // Prevenir scroll en móviles mientras se dibuja
        if (event.touches) {
            event.preventDefault();
        }

        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        
        const { x, y } = getPosition(event);
        ctx.lineTo(x, y);
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000000';
        ctx.stroke();
    },[canvasRef, getPosition, isDrawing]);

    const stopDrawing = useCallback(() => {
        if (!isDrawing) return;
        
        setIsDrawing(false);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.closePath();
        
        // Guardar la imagen como base64
        const imageData = canvas.toDataURL('image/png');
        savedImageData.current = imageData;
        setValue(imageData);
    },[canvasRef, isDrawing, setValue]);

    useEffect(() => {
        const canvas = canvasRef.current;
        const imageToLoad = sign || savedImageData.current;
        
        if (!imageToLoad || !canvas) return;
        
        console.log('load canvas', imageToLoad);
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const img = new Image();
        img.src = imageToLoad;

        img.onload = () => {
            // Usar dimensiones CSS para drawImage — correcto tanto con ctx.scale(dpr)
            // como sin él; canvas.width/height son dimensiones de buffer que causarían
            // imagen estirada en pantallas Retina con contexto escalado
            const rect = canvas.getBoundingClientRect();
            const drawW = rect.width || canvas.width;
            const drawH = rect.height || canvas.height;
            ctx.drawImage(img, 0, 0, drawW, drawH);
        };
    }, [canvasRef, sign]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Configuración para permitir preventDefault en eventos táctiles
        const touchOptions = { passive: false };

        // Agregar los eventos para ratón y táctil
        canvas.addEventListener('mousedown', startDrawing);
        canvas.addEventListener('mousemove', draw);
        canvas.addEventListener('mouseup', stopDrawing);
        canvas.addEventListener('mouseout', stopDrawing);

        canvas.addEventListener('touchstart', startDrawing, touchOptions);
        canvas.addEventListener('touchmove', draw, touchOptions);
        canvas.addEventListener('touchend', stopDrawing);
        canvas.addEventListener('touchcancel', stopDrawing);

        // Limpiar eventos al desmontar
        return () => {
            canvas.removeEventListener('mousedown', startDrawing);
            canvas.removeEventListener('mousemove', draw);
            canvas.removeEventListener('mouseup', stopDrawing);
            canvas.removeEventListener('mouseout', stopDrawing);

            canvas.removeEventListener('touchstart', startDrawing, touchOptions);
            canvas.removeEventListener('touchmove', draw, touchOptions);
            canvas.removeEventListener('touchend', stopDrawing);
            canvas.removeEventListener('touchcancel', stopDrawing);
        };
    }, [canvasRef, draw, startDrawing, stopDrawing]);

    return {
        handleClearCanvas,
    }
}



export default useDrawCanvas;